import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import Formsy from 'formsy-react';
import { Pencil, RotateCcw, Trash } from 'lucide-react';
import { Button, DefaultSheet, DefaultPopover, Separator, FormGroup, SimpleAlert, LoadingSpinner, DefaultTable, DropdownMenuItem, ButtonIcon } from 'twyne-new.ui'

import { PureSwitch } from "@Components/form/Switch"
import Input from 'components/form/Input';
import { formatDateTime } from 'utils'

import { usePostStore } from '../store';

const propTypes = {
  loadVersions: PropTypes.func,
  onView: PropTypes.func,
  number: PropTypes.number,
};

export default function Version({ data, onView, onDelete, number, onCreate, onActive }) {
  const { collection: { version: { versions } } } = usePostStore()
  const [list, setList] = useState([])

  useEffect(() => {
    versions && setList([{ isDefault: true }, ...versions])
  }, [versions])

  const handleView = data => {
    onView(data)
  }

  const handleDelete = useCallback(async id => {
    await onDelete(id)
  }, [onDelete])

  const handleCreate = useCallback(async model => {
    await onCreate(model)
  }, [onCreate])

  const handleToggleActive = useCallback(async (row, active) => {
    await onActive(row, active)
  }, [onActive])

  return <>
    <DefaultSheet
      triggerProps={{ asChild: true }}
      trigger={<DropdownMenuItem onSelect={e => e.preventDefault()} disabled={!data}>See all versions</DropdownMenuItem>}
      title='Versions'
      footer={<AddPopover onSubmit={handleCreate} />}
    >
      <Table data={list} onView={handleView} onDelete={handleDelete} versionId={number} onToggleActive={handleToggleActive} />
    </DefaultSheet>
  </>
}

function Table({ data, onView, onDelete, versionId, onToggleActive }) {
  data = data.filter(item => !item.builder)

  const columns = [
    {
      dataField: 'description',
      text: "Description",
      formatter: (description, { updatedAt, isDefault }) => {
        if (isDefault) {
          description = 'Default version'
          updatedAt = null
        } else {
          updatedAt = <span className='text-sm font-semibold twy-text-base-neutral uppercase'>
            {formatDateTime(updatedAt)}
          </span>
        }

        return <>
          <div>{description}</div>
          {updatedAt}
        </>
      },
    },
    {
      dataField: 'active',
      text: "Active",
      formatter: (active, row) => {
        if (row.isDefault) {
          return null
        }
        return <PureSwitch
          onChange={() => onToggleActive(row, !active)}
          value={active}
        />
      },
    },
    {
      dataField: 'id',
      text: "",
      className: 'w-8',
      formatter: (id, row) => {
        const isEditing = !versionId && !row.id
        return <div className='flex gap-2 pl-4'>
          {!isEditing && <>
            <ButtonIcon content={row.isDefault ? 'Restore' : 'Edit'} onClick={() => onView(row)}>
              {row.isDefault ? <RotateCcw size={16} /> : <Pencil size={16} />}
            </ButtonIcon>
          </>}

          {!row.isDefault ? <>
            <ButtonIcon content='Delete' onClick={() => onDelete(id)} >
              <Trash size={16} />
            </ButtonIcon>
          </> : null}
        </div>
      },
    },
  ]

  return <DefaultTable
    columns={columns}
    data={data || []}
    noDataIndication={() => 'No versions yet.'}
    className='max-h-[50vh] overflow-y-auto'
  />
}

function AddPopover({ onSubmit }) {
  const [open, setOpen] = useState(false)
  const toggle = () => setOpen(open => !open)

  const handleSubmit = async model => {
    await onSubmit(model)
    setOpen(false)
  }

  return <>
    <DefaultPopover
      trigger={<Button color='accent'> Create new Version </Button>}
      contentProps={{ side: 'right' }}
      wrapperProps={{ open, onOpenChange: toggle }}
    >
      <p className='font-bold'>Create new version</p>
      <Separator className='m-0 my-2' />
      <>
        The new version will be created from the current post settings (content, title, template, etc..). Please type a description of this new version.
        <AddForm onSubmit={handleSubmit} />
      </>
    </DefaultPopover>
  </>
}

function AddForm({ onSubmit }) {
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async model => {
    setLoading(true)
    try {
      await onSubmit(model)
    } catch (e) {
      setError(true)
    }
    setLoading(false)
  }

  return <Formsy onValidSubmit={handleSubmit}>
    <FormGroup className='pt-2'>
      <Input required name='description' />
    </FormGroup>

    {error && <>
      <SimpleAlert variant='danger'>
        We can not create a new version
      </SimpleAlert>
    </>}
    <Button type='submit' block className='flex items-center w-full mt-4' color="accent" disabled={loading}>
      {loading ? <LoadingSpinner width={18} height={18} /> : 'Save'}
    </Button>
  </Formsy>
}

Version.propTypes = propTypes
